import { variantIdToGid } from './availability';
import { fetchCartVariantsAvailability } from './storefront-api';
import { saveShopLocation } from './storage';
import { syncStoreLocatorLabels } from './labels';

let pendingStoreSelection = null;

function getDrawer() {
  return document.getElementById('store-locator-cart-conflict-drawer');
}

function getElements(drawer) {
  return {
    items: drawer.querySelector('.js-store-locator-conflict-items'),
    error: drawer.querySelector('.js-store-locator-conflict-error'),
    continueButton: drawer.querySelector('.js-store-locator-conflict-continue'),
    cancelButton: drawer.querySelector('.js-store-locator-conflict-cancel'),
  };
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function commitStoreSelection(location) {
  saveShopLocation(location);
  syncStoreLocatorLabels();

  document.dispatchEvent(
    new CustomEvent('store-locator:shop-changed', {
      bubbles: true,
      detail: location,
    })
  );

  pendingStoreSelection = null;
  document.getElementById('store-locator-shop-drawer')?.close();
  getDrawer()?.close();
}

function renderConflictItems(elements, lineItems) {
  elements.items.innerHTML = lineItems
    .map(
      (item) => `
        <div class="store-locator-conflict-item">
          <img class="store-locator-conflict-item__image" src="${escapeHtml(item.image)}" alt="" width="61" height="61" loading="lazy">
          <p class="store-locator-conflict-item__title">${escapeHtml(item.title)}</p>
        </div>
      `
    )
    .join('');
}

function setConflictError(elements, message) {
  if (!message) {
    elements.error.hidden = true;
    elements.error.textContent = '';
    return;
  }

  elements.error.hidden = false;
  elements.error.textContent = message;
}

function openConflictDrawer(unavailableItems) {
  const drawer = getDrawer();

  if (!drawer) {
    commitStoreSelection(pendingStoreSelection);
    return;
  }

  const elements = getElements(drawer);
  setConflictError(elements, null);
  renderConflictItems(elements, unavailableItems);
  drawer.open();
}

export async function requestStoreSelection(location) {
  pendingStoreSelection = location;

  try {
    const cartResponse = await fetch(`${window.theme.routes.cart}.js`);

    if (!cartResponse.ok) {
      throw new Error('Unable to load cart.');
    }

    const cart = await cartResponse.json();
    const lineItems = cart.items ?? [];

    if (lineItems.length === 0) {
      commitStoreSelection(location);
      return;
    }

    const variantGids = [...new Set(lineItems.map((item) => variantIdToGid(item.variant_id)))];
    const availabilityByVariantGid = await fetchCartVariantsAvailability(variantGids, location.id);

    const unavailableItems = lineItems.filter((item) => {
      const variantGid = variantIdToGid(item.variant_id);
      const availability = availabilityByVariantGid.get(variantGid);
      return !availability?.available;
    });

    if (unavailableItems.length === 0) {
      commitStoreSelection(location);
      return;
    }

    openConflictDrawer(
      unavailableItems.map((item) => ({
        title: item.product_title,
        image: item.image ?? item.featured_image?.url ?? '',
      }))
    );
  } catch (conflictError) {
    console.error('Store locator cart conflict check failed:', conflictError);
    const drawer = getDrawer();
    const elements = drawer ? getElements(drawer) : null;

    if (elements) {
      setConflictError(elements, 'Unable to verify cart availability. Please try again.');
      drawer.open();
    }
  }
}

export function initCartConflictDrawer() {
  const drawer = getDrawer();

  if (!drawer || drawer.dataset.storeLocatorConflictInit) return;

  drawer.dataset.storeLocatorConflictInit = 'true';
  const elements = getElements(drawer);

  elements.continueButton?.addEventListener('click', () => {
    if (pendingStoreSelection) {
      commitStoreSelection(pendingStoreSelection);
    }
  });

  elements.cancelButton?.addEventListener('click', () => {
    pendingStoreSelection = null;
    setConflictError(elements, null);
    drawer.close();
  });

  drawer.addEventListener('on:store-locator-cart-conflict:after-close', () => {
    pendingStoreSelection = null;
    setConflictError(elements, null);
    elements.items.innerHTML = '';
  });
}
